import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../config/firebase';

export interface UploadResult {
  url: string;
  path: string;
}

/**
 * Upload an image to Firebase Storage
 * @param file - The file to upload
 * @param folder - The folder path (e.g., 'cars', 'brands')
 * @param fileName - Optional custom filename
 * @returns Promise with download URL and storage path
 */
export const uploadImage = async (
  file: File,
  folder: string = 'cars',
  fileName?: string
): Promise<UploadResult> => {
  try {
    // Validate file type
    const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!file.type.startsWith('image/') && !supportedTypes.includes(file.type)) {
      throw new Error('File must be an image (JPEG, PNG, WebP supported)');
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error('File size must be less than 5MB');
    }

    // Generate unique filename if not provided
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 15);
    const fileExtension = file.name.split('.').pop();
    const finalFileName = fileName || `${timestamp}_${randomString}.${fileExtension}`;

    // Create storage reference
    const storageRef = ref(storage, `${folder}/${finalFileName}`);

    // Upload file
    const snapshot = await uploadBytes(storageRef, file);
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    return {
      url: downloadURL,
      path: snapshot.ref.fullPath,
    };
  } catch (error: any) {
    console.error('Error uploading image:', error);
    throw new Error(error.message || 'Failed to upload image');
  }
};

/**
 * Upload multiple images to Firebase Storage
 * @param files - Array of files to upload
 * @param folder - The folder path
 * @returns Promise with array of upload results
 */
export const uploadMultipleImages = async (
  files: File[],
  folder: string = 'cars'
): Promise<UploadResult[]> => {
  try {
    const uploadPromises = files.map(file => uploadImage(file, folder));
    return await Promise.all(uploadPromises);
  } catch (error: any) {
    console.error('Error uploading multiple images:', error);
    throw new Error(error.message || 'Failed to upload images');
  }
};

/**
 * Delete an image from Firebase Storage
 * @param imagePath - The full path of the image in storage
 */
export const deleteImage = async (imagePath: string): Promise<void> => {
  try {
    const imageRef = ref(storage, imagePath);
    await deleteObject(imageRef);
  } catch (error: any) {
    console.error('Error deleting image:', error);
    throw new Error(error.message || 'Failed to delete image');
  }
};

/**
 * Delete multiple images from Firebase Storage
 * @param imagePaths - Array of image paths to delete
 */
export const deleteMultipleImages = async (imagePaths: string[]): Promise<void> => {
  try {
    const deletePromises = imagePaths.map(path => deleteImage(path));
    await Promise.all(deletePromises);
  } catch (error: any) {
    console.error('Error deleting multiple images:', error);
    throw new Error(error.message || 'Failed to delete images');
  }
};

/**
 * Get optimized image URL with size parameters
 * @param originalUrl - The original Firebase Storage URL
 * @param width - Desired width
 * @param height - Desired height
 * @returns Optimized image URL (Note: Firebase doesn't have built-in image optimization)
 */
export const getOptimizedImageUrl = (
  originalUrl: string,
  width?: number,
  height?: number
): string => {
  // Firebase Storage doesn't have built-in image optimization
  // You could integrate with a service like Cloudinary or implement your own
  // For now, return the original URL
  return originalUrl;
};

/**
 * Validate image file before upload
 * @param file - File to validate
 * @returns Validation result
 */
export const validateImageFile = (file: File): { isValid: boolean; error?: string } => {
  // Check file type
  if (!file.type.startsWith('image/')) {
    return { isValid: false, error: 'File must be an image' };
  }

  // Check file size (max 5MB)
  const maxSize = 5 * 1024 * 1024;
  if (file.size > maxSize) {
    return { isValid: false, error: 'File size must be less than 5MB' };
  }

  // Check supported formats
  const supportedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!supportedFormats.includes(file.type)) {
    return { isValid: false, error: 'Supported formats: JPEG, PNG, WebP' };
  }

  return { isValid: true };
};
